

















































































































































































































import CoSelect from '@/components/Molecules/co-select/CoSelect.vue';
import CoMultiSelect from '@/components/Molecules/co-multiselect/CoMultiSelect.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import axios from 'axios';
import { get, map, uniq, cloneDeep, values } from 'lodash';
import {
    Rule,
    OperatorOptions,
    EQ,
    NEQ,
    GT,
    GTE,
    LT,
    LTE,
    LIKE,
    NOTLIKE,
    BETWEEN,
    ANY,
    ConditionField,
    AccessCondition,
    AccessConditionGroup,
} from './models.ts';

export default {
    name: 'CoRules',
    components: {
        CoSelect,
        CoMultiSelect,
        CoButton,
        CoRoundButton,
        CoInput,
    },
    props: {
        rules: {
            type: Object as () => Rule,
            default: () => ({}),
        },
        forceOr: {
            type: Boolean,
            default: false,
        },
        lockFirstConditionGroup: {
            type: Boolean,
            default: false,
        },
        hideOperator: {
            type: Boolean,
            default: false,
        },
        lockAdminRole: {
            type: Boolean,
            default: false,
        },
        allowedConditionFields: {
            type: Array,
            default: () => [],
        },
        defaultConditionField: {
            type: String,
            default: 'user_id',
        },
        uniqueConditionFields: {
            type: Boolean,
            default: false,
        },
        permissionPlans: {
            type: Array,
            default: () => [],
        },
        permissionCustomFields: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            rulesToModify: cloneDeep(this.rules) as Rule,
            rerenderKey: 0, // Used to force re-render if needed
            syncingRulesFromProp: false,
            lastEmittedRuleSignature: '',
            availablePlans: [], // list of available membership plans
        };
    },
    watch: {
        permissionPlans: {
            handler(plans) {
                if (plans.length > 0) {
                    this.availablePlans = plans;
                    this.prefillPlansInCondition();
                }
            },
            deep: true,
        },
        rules: {
            handler(newRules) {
                this.syncRulesFromProp(newRules);
            },
            deep: true,
            immediate: true,
        },
        rulesToModify: {
            handler(newRules) {
                if (this.syncingRulesFromProp) {
                    return;
                }

                const serializedRules = this.serializeRule(newRules);
                if (serializedRules === this.lastEmittedRuleSignature) {
                    return;
                }

                // Emit only the business-rule payload so UI-only prefetched values stay local.
                this.lastEmittedRuleSignature = serializedRules;
                this.$emit('update', this.normalizeRule(newRules));
            },
            deep: true,
        },
    },
    computed: {
        operators() {
            return Object.values(OperatorOptions).map((op) => ({
                value: op,
                label: this.$t(`labels.rules.operators.${op}`),
            }));
        },
        conditionFields() {
            const fields = {
                user_id: this.$t('labels.rules.fields.user_id'),
                plan_id: this.$t('labels.rules.fields.plan_id'),
                user_role: this.$t('labels.rules.fields.user_role'),
                custom: this.$t('labels.permissionsRules.custom'),
                // user_status: this.$t('labels.rules.fields.user_status'),
                // user_email: this.$t('labels.rules.fields.user_email'),
            };
            return fields;
        },
        selectableConditionFields() {
            if (this.allowedConditionFields.length === 0) return this.conditionFields;
            const availableFields = Object.entries(this.conditionFields) as Array<[string, string]>;
            return availableFields.reduce((fields: Record<string, string>, [field, label]) => {
                if (this.allowedConditionFields.includes(field)) fields[field] = label;
                return fields;
            }, {} as Record<string, string>);
        },
        customFieldOptions() {
            return this.permissionCustomFields.map((field) => ({
                Name: field.label || field.Label || field.identifier || field.Identifier,
                Value: field.identifier || field.Identifier,
            }));
        },
    },
    mounted() {
        if (this.permissionPlans.length > 0) {
            this.availablePlans = this.permissionPlans;
            return;
        }
        this.getAllPlans(); // Fetch all plans on mount
    },
    methods: {
        conditionFieldOptions(ruleIndex: number) {
            const selectedFields = this.selectedConditionFields(ruleIndex);
            return Object.entries(this.selectableConditionFields).map(([key, field]) => ({
                Value: key,
                Name: field,
                Disabled: this.uniqueConditionFields && selectedFields.includes(key),
            }));
        },
        selectedConditionFields(excludedRuleIndex?: number): string[] {
            return this.rulesToModify.ConditionsGroups.filter((_, index) => index !== excludedRuleIndex).reduce(
                (fields, group) => fields.concat((group.Conditions || []).map((condition) => String(condition.Field))),
                [] as string[]
            );
        },
        nextConditionField() {
            if (!this.uniqueConditionFields) return this.defaultConditionField;
            return (
                this.allowedConditionFields.find((field) => !this.selectedConditionFields().includes(field)) ||
                this.defaultConditionField
            );
        },
        normalizeRule(rule: Rule = {} as Rule): Rule {
            return {
                CombineWithOR: !!rule?.CombineWithOR,
                ConditionsGroups: (rule?.ConditionsGroups || []).map((group) => ({
                    CombineWithOR: !!group?.CombineWithOR,
                    Conditions: (group?.Conditions || []).map((condition) => ({
                        Field: condition.Field,
                        CustomFieldName: condition.CustomFieldName,
                        Operator: condition.Operator,
                        Values: condition.Values ? [...condition.Values] : [],
                    })),
                })),
            };
        },
        serializeRule(rule: Rule): string {
            return JSON.stringify(this.normalizeRule(rule));
        },
        syncRulesFromProp(newRules: Rule) {
            const normalizedRules = this.normalizeRule(newRules || {});
            const incomingRuleSignature = this.serializeRule(normalizedRules);
            if (incomingRuleSignature === this.lastEmittedRuleSignature) {
                return;
            }

            // Keep parent-to-child sync separate from local edits so reflected updates do not loop back.
            this.syncingRulesFromProp = true;
            this.lastEmittedRuleSignature = incomingRuleSignature;
            this.rulesToModify = cloneDeep(normalizedRules);
            this.hydrateConditionPrefetchedValues();
            this.$nextTick(() => {
                this.syncingRulesFromProp = false;
            });
        },
        hydrateConditionPrefetchedValues() {
            (this.rulesToModify.ConditionsGroups || []).forEach((group) => {
                (group.Conditions || []).forEach((condition) => {
                    if (condition.Field === 'user_id' && condition.Values?.length > 0) {
                        this.fetchUsersForCondition(condition);
                    }

                    if (condition.Field === 'user_role' && condition.Values?.length > 0) {
                        this.$set(
                            condition,
                            'PrefetchedValues',
                            values(condition.Values).map((role) => ({
                                Name: this.$t(`labels.roles.${role}`),
                                Value: role,
                            }))
                        );
                    }

                    if (condition.Field === 'plan_id' && condition.Values?.length > 0) {
                        this.$set(
                            condition,
                            'PrefetchedValues',
                            this.availablePlans.filter((plan) => condition.Values.includes(plan.Value))
                        );
                    }
                });
            });
        },
        setConditionValueForUserRole(condition: AccessCondition, $event: any) {
            condition.Values = $event.map((i) => i.Value);
            condition.PrefetchedValues = $event;
        },
        setConditionCustomFieldName(condition: AccessCondition, value: string) {
            this.$set(condition, 'CustomFieldName', value);
        },
        setConditionValueForCustomField(condition: AccessCondition, value: string) {
            this.$set(
                condition,
                'Values',
                value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean)
            );
        },
        selectedCustomField(condition: AccessCondition) {
            return this.permissionCustomFields.find(
                (field) => (field.identifier || field.Identifier) === condition.CustomFieldName
            );
        },
        selectedCustomFieldOption(condition: AccessCondition) {
            return this.customFieldOptions.find((field) => field.Value === condition.CustomFieldName);
        },
        isSelectableCustomField(condition: AccessCondition) {
            const dataType = this.selectedCustomField(condition)?.data_type;
            return ['select', 'multiselect', 'boolean'].includes(dataType);
        },
        customFieldValueOptions(condition: AccessCondition) {
            const field = this.selectedCustomField(condition);
            if (field?.data_type === 'boolean') {
                return [
                    { Name: 'True', Value: 'true' },
                    { Name: 'False', Value: 'false' },
                ];
            }
            return (field?.options_values || []).map((value) => ({ Name: value, Value: value }));
        },
        selectedCustomFieldValues(condition: AccessCondition) {
            return this.customFieldValueOptions(condition).filter((item) => condition.Values?.includes(item.Value));
        },
        setConditionCustomField(condition: AccessCondition, identifier: string) {
            this.$set(condition, 'CustomFieldName', identifier);
            this.$set(condition, 'Values', []);
        },
        setConditionValueForCustomFieldOptions(condition: AccessCondition, values: any[]) {
            this.$set(
                condition,
                'Values',
                values.map((item) => item.Value)
            );
        },
        setConditionValueForPlanID(condition: AccessCondition, $event: any) {
            condition.Values = $event.map((i) => i.Value);
            condition.PrefetchedValues = $event;
        },
        prefillPlansInCondition() {
            (this.rulesToModify.ConditionsGroups || []).forEach((group) => {
                (group.Conditions || []).forEach((condition) => {
                    if (condition.Field === 'plan_id' && condition.Values?.length > 0) {
                        condition.PrefetchedValues = this.availablePlans.filter((plan) =>
                            condition.Values.includes(plan.Value)
                        );
                        this.rerenderKey += 1; // Force re-render to update the UI
                    }
                    this.rerenderKey += 1; // Force re-render to update the UI
                });
            });
        },
        getAllPlans() {
            // Fetch all plans from the server
            axios({
                method: 'GET',
                url: '/admin/plan/list',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    const planList = get(response, 'data.Plans', []);
                    this.availablePlans = planList.map((plan) => ({
                        Name: plan.Name,
                        Value: plan.ID,
                        ThumbnailImageURL: plan.ImageURL || '',
                        ShowThumbnail: true,
                    }));
                    this.prefillPlansInCondition();
                })
                .catch((error) => {
                    console.error('Error fetching plans:', error);
                });
        },
        setConditionValueForUserID(condition: AccessCondition, $event: any) {
            condition.Values = $event.map((i) => i.Value);
            condition.PrefetchedValues = $event;
        },
        fetchUsersForCondition(condition: AccessCondition) {
            let userIDs = condition.Values || [];
            userIDs = uniq(userIDs);
            // get the users
            axios({
                method: 'POST',
                url: '/user/listbyids',
                data: {
                    IDS: map(userIDs, (i) => ({ ID: i })),
                },
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    const objects = get(response, 'data.Users', []);
                    const prefetchedValues = objects.map((user: any) => ({
                        Name: get(user, 'Profile.Name', 'Unknown User'),
                        Value: user.ID,
                        Disabled: false,
                        ShowThumbnail: true,
                        RoundThumbnail: true,
                        ThumbnailImageURL: get(user, 'Profile.ImageURL', ''),
                    }));
                    // Instead of assigning to parameter, emit or update via Vue reactivity
                    condition.PrefetchedValues = prefetchedValues;
                    this.rerenderKey += 1; // Force re-render to update the UI
                })
                .catch((error) => {
                    this.rerenderKey += 1; // Force re-render to update the UI
                    console.error('Error fetching users:', error);
                });
        },
        searchUsers(query: string, source: any) {
            return new Promise((resolve, reject) => {
                // exit if query is less than 2 characters
                if (!query || query.length < 2) {
                    resolve([]);
                    return;
                }
                axios({
                    method: 'GET',
                    url: `/admin/member/search/${query}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    cancelToken: source ? source.token : null,
                })
                    .then((response) => {
                        const objects = get(response, 'data.objects', []);
                        const output = objects.map((user: any) => ({
                            Name: get(user, 'object.Profile.Name', 'Unknown User'),
                            Value: user.object.ID,
                            Disabled: false,
                            ShowThumbnail: true,
                            RoundThumbnail: true,
                            ThumbnailImageURL: get(user, 'object.Profile.ImageURL', ''),
                        }));
                        resolve(output);
                    })
                    .catch((error) => {
                        console.error('Error fetching users:', error);
                        reject(error);
                    });
            });
        },
        addNewGroup() {
            this.rulesToModify.ConditionsGroups.push({
                Conditions: [
                    {
                        Field: this.nextConditionField(),
                        Operator: EQ,
                        Values: [],
                    },
                ],
                CombineWithOR: false,
            });
            if (this.forceOr) this.rulesToModify.CombineWithOR = true;
        },
        addCondition(rule: AccessConditionGroup, condIndex: number) {
            rule.Conditions.push({
                Field: this.defaultConditionField,
                Operator: EQ,
                Values: [],
            });
            if (this.forceOr) rule.CombineWithOR = true;
        },
        removeCondition(rule: AccessConditionGroup, condIndex: number) {
            rule.Conditions.splice(condIndex, 1);
            if (rule.Conditions.length === 0) {
                this.rulesToModify.ConditionsGroups.splice(this.rulesToModify.ConditionsGroups.indexOf(rule), 1);
            }
        },
        changeGroupConditionType(rule: AccessConditionGroup, type: 'AND' | 'OR') {
            rule.CombineWithOR = type === 'OR';
        },
        operatorsForField(field: ConditionField) {
            switch (field) {
                case 'user_id':
                case 'plan_id':
                case 'user_status':
                case 'user_role':
                case 'custom':
                    return {
                        EQ: this.$t('labels.rules.operators.EQ'),
                        NEQ: this.$t('labels.rules.operators.NEQ'),
                        ANY: this.$t('labels.rules.operators.ANY'),
                    };
                default:
                    return [];
            }
        },
        // You can add methods to handle any specific logic related to rules
        optionsForField(field: string, value: string) {
            switch (field) {
                case 'user_id':
                    return this.getUsers(value);
                case 'plan_id':
                    return this.getPlans(value);
                case 'user_role':
                    return this.getUserRoles(value);
                case 'user_status':
                    return this.getUserStatuses(value);
                default:
                    return [];
            }
        },
        getUsers(input: string) {
            return [];
        },
        getPlans(input: string) {
            return [];
        },
        getUserRoles(input: string) {
            return [];
        },
        getUserStatuses(input: string) {
            return [];
        },
    },
};
